import React, { useState, useEffect, useCallback } from 'react'
import Modal from 'react-responsive-modal'
import styles from './styles.module.css'
import { planningModalModes } from '../../assets/const'
import cn from 'classnames'
import { Planning, Homework, Files, About, Literature } from './ModalTabs'
import ModalFooter from '../../components/ModalFooter/ModalFooter'
import { useSelector, useDispatch } from 'react-redux'
import {
    saveThemePlanningLesson,
    getThemePlanningLesson,
    getThemePlanningEventTypes,
    getThemePlanningWeights,
    getThemePlanningThemes,
    getThemePlanningTrials,
    getThemePlanning
} from '../../actions/planning'
import Loader from '../../components/Loader'
import Confirmation from '../../components/Confirmation/Confirmation'

const formatDateTime = (isoString) => {
    if (!isoString) return ''
    const date = new Date(isoString)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes}`
}

export default function ModalComponent({ isOpen, closeModal, handleSubmit, selectedLesson, formData }) {

    const [mode, setMode] = useState('planning')
    const [lessonData, setLessonData] = useState({})
    const [errors, setErrors] = useState({})
    const [confirmClose, setConfirmClose] = useState(false)
    const handleClose = useCallback(() => setConfirmClose(true), [])
    const acceptConfirmation = useCallback(() => { closeModal(); setConfirmClose(false) }, [closeModal])
    const rejectConfirmation = useCallback(() => setConfirmClose(false), [])

    const dispatch = useDispatch()
    const { lesson, modalLoading, eventTypes, weights, themes, trials } = useSelector(state => state.planning)

    useEffect(() => {
        if (isOpen && selectedLesson) {
            dispatch(getThemePlanningEventTypes(formData.type))
            dispatch(getThemePlanningWeights())
            if (selectedLesson.sheid && selectedLesson.week_id) {
                dispatch(getThemePlanningLesson(selectedLesson.sheid, selectedLesson.week_id, formData.type))
                dispatch(getThemePlanningTrials(selectedLesson.sheid, selectedLesson.week_id))
            }
        }
    }, [isOpen, selectedLesson])

    useEffect(() => {
        if (lesson) {
            setLessonData({
                sheid: lesson.sheid,
                week_id: lesson.week_id,
                cid: lesson.cid,
                gid: lesson.gid,
                teacher_mid: lesson.teacher_mid,
                year_id: lesson.year_id,
                event_type: lesson.event_type || 0,
                module_id: lesson.module_id || 0,
                date_fact: lesson.date_fact || '',
                default_weight: lesson.default_weight || 1,
                trial: lesson.trial || 0,
                hw_text: lesson.hw_text || '',
                hw_hours: lesson.hw_hours || 0,
                hw_minutes: lesson.hw_minutes || 0,
                hw_reqresult: lesson.hw_reqresult || 0,
                comment: lesson.comment || '',
                explanation: lesson.explanation || '',
                literature: lesson.literature || '',
                work_count: lesson.work_count || 1,
            })
            if (lesson.cid && lesson.teacher_mid && lesson.year_id) {
                dispatch(getThemePlanningThemes(
                    lesson.cid,
                    lesson.teacher_mid,
                    lesson.year_id,
                    lesson.gid,
                    lesson.sheid,
                    formData.type
                ))
            }
        }
    }, [lesson])

    useEffect(() => {
        if (!isOpen) {
            setMode('planning')
            setLessonData({})
            setErrors({})
        }
    }, [isOpen])

    const handleLessonDataChange = (field, value) => {
        setLessonData(prev => ({ ...prev, [field]: value }))
    }

    const handleSave = async () => {
        const newErrors = {}
        if (!lessonData.event_type) {
            newErrors.event_type = 'Обязательное поле'
        }
        if (!lessonData.module_id) {
            newErrors.module_id = 'Обязательное поле'
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            setMode('planning')
            return
        }

        setErrors({})

        const body = {
            sheid: lessonData.sheid,
            week_id: lessonData.week_id,
            gid: lessonData.gid,
            teacher_mid: lessonData.teacher_mid,
            event_type: lessonData.event_type,
            module_id: lessonData.module_id,
            date_fact: lessonData.date_fact || null,
            default_weight: lessonData.default_weight,
            trial_id: lessonData.trial || 0,
            hw_text: lessonData.hw_text || '',
            hw_hours: lessonData.hw_hours || 0,
            hw_minutes: lessonData.hw_minutes || 0,
            hw_reqresult: lessonData.hw_reqresult || 0,
            comment: lessonData.comment || '',
            explanation: lessonData.explanation || '',
            literature: lessonData.literature || '',
            work_count: lessonData.work_count || 1,
            del_lesson_title: false
        }

        const success = await dispatch(saveThemePlanningLesson(body, formData.type))
        if (success) {
            dispatch(getThemePlanning(formData.teacher, formData.lesson || 0, formData.week, formData.type))
            handleSubmit()
        }
    }

    const handleClearTheme = async () => {
        const body = {
            sheid: lessonData.sheid,
            week_id: lessonData.week_id,
            del_lesson_title: true
        }

        const success = await dispatch(saveThemePlanningLesson(body, formData.type))
        if (success) {
            dispatch(getThemePlanning(formData.teacher, formData.lesson || 0, formData.week, formData.type))
            handleSubmit()
        }
    }

    const renderLi = ({ name, value, icon }) => {
        const selected = value === mode
        return (
            <li
                key={value}
                onClick={() => setMode(value)}
                className={cn(styles.modalMenuItem, {
                    [styles.selectedMenuItem]: selected
                })}
            >
                {icon()}
                {name}
            </li>
        )
    }

    const renderTab = () => {
        if (modalLoading) {
            return <Loader />
        }

        const tabProps = {
            lesson,
            lessonData,
            handleLessonDataChange,
            eventTypes,
            weights,
            themes,
            trials,
            selectedLesson,
            formData,
            errors
        }

        switch (mode) {
            case 'planning': return <Planning {...tabProps} />
            case 'homework': return <Homework {...tabProps} />
            case 'files': return <Files {...tabProps} />
            case 'about': return <About {...tabProps} />
            case 'literature': return <Literature {...tabProps} />
            default: return null
        }
    }

    const isHoliday = lesson?.is_holiday

    return (
        <Modal
            open={isOpen}
            onClose={handleClose}
            classNames={{ modal: styles.modalMain }}
            closeOnOverlayClick={true}
            closeOnEsc={true}
            showCloseIcon={false}
            animationDuration={1}
        >
            <div className={styles.modalContent}>
                <div className={styles.modalMenu}>
                    <ul>{planningModalModes.map(renderLi)}</ul>
                </div>
                <div className={styles.modalMain}>
                    {isHoliday && (
                        <div className={styles.holidayWarning}>
                            Редактирование запрещено - праздничный день
                        </div>
                    )}
                    {renderTab()}
                </div>
                <div className={styles.modalFooter}>
                    <div className={styles.modalFooterInfo}>
                        {lesson?.fio_changed && (
                            <div>Изменил: {lesson.fio_changed}</div>
                        )}
                        {lesson?.date_changed && (
                            <div>Дата изменения: {formatDateTime(lesson.date_changed)}</div>
                        )}
                    </div>
                    {/* <div>
                        {mode === 'planning' && !isHoliday && (
                            <button
                                className={styles.clearButton}
                                onClick={handleClearTheme}
                            >
                                Очистить тип и тему занятия
                            </button>
                        )}
                    </div> */}
                    <ModalFooter
                        handleSubmit={isHoliday ? closeModal : handleSave}
                        closeModal={closeModal}
                        submitDisabled={isHoliday}
                    />
                </div>
            </div>
            {confirmClose && (
                <Confirmation
                    title="Закрыть без сохранения изменений?"
                    acceptConfirmation={acceptConfirmation}
                    rejectConfirmation={rejectConfirmation}
                />
            )}
        </Modal>
    )
}
